<template>
  <div
    ref="main"
    :class="containerClasses"
    :style="cssVariableStyles"
  >
    <transition name="enlarge" @after-leave="() => {if (!bubble.isOpen) notifyClosed()}">
      <div
        v-if="bubble.isOpen"
        class="main-wrapper"
      >
        <div class="main-wrapper__tab-content">
          <chat-tab
            v-if="isTab('chat')"
            :show-close-button="showCloseButton"
            :is-typing-enabled="isTypingEnabled"
            @toggle-chat="toggleChat"
          />
          <wizard-tab v-if="isTab('wizard')" />
        </div>
        <div class="main-wrapper__footer">
          <feature-flag name="one_solution">
            <div class="main-wrapper__tab-bar">
              <tab-bar
                :current-tab="currentTab"
                @tab-change="onTabChanged"
              />
            </div>
          </feature-flag>
          <div class="main-wrapper__privacy-powered">
            <chat-footer
              :customer-id="customerId"
              :privacy-policy="privacyPolicy"
              :chatchamp-link-clickable="channel.chatchampLinkClickable"
            />
          </div>
        </div>
      </div>
    </transition>

    <div
      v-if="channel"
      class="bubble-container"
    >
      <chat-bubble
        ref="chatBubble"
        :is-open="bubble.isOpen"
        @click="toggleChat"
      />
      <transition name="enlarge" @after-enter="notifyWelcomeMessageShown">
        <welcome-message
          v-if="isShowingWelcomeMessage"
          :message="welcomeMessage.welcomeMessage"
          @click.once="welcomeMessageClick"
        />
      </transition>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import { logger, getQuery } from '../../../common'
import { insertStyle } from '../../../common/utils'
import { scrollToLastMessage } from '../../../common/scrolling'
import { CHATBOT_EVENTS, dispatchChatchampEvent } from '../../../main/eventTracking'
import { loadFeatureFlags } from '../../../main/eventTracking/posthog'
import {
  requestMessageHistory,
  sendLoadedMessage,
  sendClickedMessage,
  sendAutostartMessage
} from '../../utils/api'
import { postMessageMixin, webSocketMixin } from '../../mixins'
import { WelcomeMessage, ChatBubble, ChatFooter } from '..'

import { TabBar } from './TabBar'
import { ChatTab, WizardTab } from './TabContents'

export default {
  components: {
    WelcomeMessage,
    ChatBubble,
    ChatFooter,
    TabBar,
    ChatTab,
    WizardTab
  },
  mixins: [postMessageMixin, webSocketMixin],
  props: {
    showCloseButton: {
      default: true,
      type: Boolean
    },
    alignBotDesktop: {
      default: 'right',
      type: String
    },
    alignBotMobile: {
      default: 'right',
      type: String
    }
  },
  data: () => {
    return {
      isLoading: true,
      isLoadedCustomCss: false,
      hasTrackedStarted: false
    }
  },
  computed: {
    ...mapGetters(['primaryColor', 'customCss', 'privacyPolicy', 'isShowingWelcomeMessage', 'isShowingOpenChat', 'closedByUserRecently', 'chatbotId', 'isEnabledABTest']),
    ...mapState(['channel', 'customerId', 'subscriberId', 'isTypingEnabled', 'welcomeMessage', 'bubble', 'parentUrl', 'device', 'currentTab']),
    cssVariableStyles () {
      return {
        '--primary-color': this.primaryColor,
        opacity: this.isLoadedCustomCss ? 1 : 0
      }
    },
    containerClasses () {
      return {
        'chatchamp-container': true,
        'is-open': this.bubble.isOpen,
        'chatchamp-container--align-bot-left-mobile': this.alignBotMobile === 'left',
        'chatchamp-container--align-bot-left-desktop': this.alignBotDesktop === 'left'
      }
    }
  },
  watch: {
    'bubble.isOpen' (newValue: boolean): void {
      if (newValue === false) {
        this.notifyClosing()
        setTimeout(() => this.focusChatBubble(), 300)
      } else if (newValue === true) {
        this.notifyOpening()
        setTimeout(() => this.focusFirstInteractiveElement(), 300)
      }
    },
    'isShowingOpenChat' (newValue: boolean, oldValue: boolean): void {
      // when the user does not click and another exit-intent welcome message is set
      if (oldValue && !newValue && !this.bubble.hasWebsiteChatBeenOpened) {
        this.$store.dispatch('close')
      }
    },
    async 'welcomeMessage.welcomeMessage' (newWelcomeMessage: WelcomeMessage, oldWelcomeMessage: WelcomeMessage): Promise<void> {
      if (!newWelcomeMessage) return

      if (this.isShowingOpenChat && !!oldWelcomeMessage) {
        await this.$store.dispatch('replaceOpenChat', newWelcomeMessage)
        this.notifyWelcomeMessageShown()
        return
      }

      if (this.isShowingWelcomeMessage && newWelcomeMessage.type === 'open_chat') {
        await this.$store.dispatch('showOpenChat', newWelcomeMessage)
        this.notifyWelcomeMessageShown()
      }
    },
    async 'closedByUserRecently' (value: boolean): Promise<void> {
      if (value === true) {
        await this.$nextTick()
        this.notifyClosed()
      }
    },
    'channel.locale' (val: string): void {
      this.$root.$i18n.locale = val
    },
    chatbotId (value: ?number): void {
      if (value && this.bubble.isOpenInitial && !this.hasTrackedStarted) {
        this.hasTrackedStarted = true
        dispatchChatchampEvent(CHATBOT_EVENTS.Started, {
          customerId: this.customerId,
          subscriberId: this.subscriberId,
          chatbotId: value,
          trigger: 'automatic',
          parentUrl: this.parentUrl
        })
      }
    }
  },
  async mounted () {
    try {
      logger.info('Initializing chat main component...')

      // Element was hidden in DOM
      if (!this.$el.offsetParent) {
        logger.info('Chatchamp website chat was hidden by external code.')
        return
      }

      await this.$store.dispatch('loadConfig')
      if (this.customCss) insertStyle(this.customCss)
      this.isLoadedCustomCss = true

      await this.initWebSocket(this.subscriberId, this.customerId, this.parentUrl)
      if (!this.isShowingOpenChat) await requestMessageHistory()
      await sendLoadedMessage()
      if (this.bubble.isOpenInitial) {
        this.focusFirstInteractiveElement()
        await sendAutostartMessage(getQuery('chatbotPayload'))
      }

      this.notifyLoaded()
      this.isLoading = false

      logger.info(`Welcome Message shown: ${this.isShowingWelcomeMessage}`)
      logger.info('Finished initializing chat main component.')

      this.installFocusTrap()
    } catch (error) {
      logger.error(error)
    }
  },
  methods: {
    ...mapActions(['setCurrentTab']),
    async toggleChat (): Promise<void> {
      // open chat was closed
      if (this.bubble.isOpen && this.isShowingOpenChat) {
        this.$store.dispatch('welcomeMessageClosed')
        this.notifyWelcomeMessageClosed()
      } else {
        if (this.isShowingWelcomeMessage) {
          dispatchChatchampEvent(CHATBOT_EVENTS.WelcomeMessageClick, {
            customerId: this.$store.state.customerId,
            subscriberId: this.$store.state.subscriberId,
            welcomeMessageId: this.$store.state.welcomeMessage?.welcomeMessage.id,
            device: this.$store.state.device,
            welcomeMessageBubbleClick: true
          })
        }
        if (await this.$store.dispatch('redirectWelcomeMessageToWizard')) return
      }

      this.$store.dispatch('toggleOpen')

      if (!this.isShowingOpenChat) {
        if (this.bubble.isOpen) {
          requestMessageHistory()
          sendClickedMessage(this.welcomeMessage.welcomeMessage?.id, getQuery('chatbotPayload'))
          this.notifyClicked()
          this.$nextTick(scrollToLastMessage)
          this.loadFeatureFlags()
          dispatchChatchampEvent(CHATBOT_EVENTS.Started, {
            customerId: this.customerId,
            subscriberId: this.subscriberId,
            chatbotId: this.chatbotId,
            trigger: 'manual',
            parentUrl: this.parentUrl
          })
        } else {
          dispatchChatchampEvent(CHATBOT_EVENTS.CloseChatClick, {
            customerId: this.customerId,
            subscriberId: this.subscriberId,
            device: this.$store.state.device
          })
        }
      }
    },
    async welcomeMessageClick (): Promise<void> {
      dispatchChatchampEvent(CHATBOT_EVENTS.WelcomeMessageClick, {
        customerId: this.$store.state.customerId,
        subscriberId: this.$store.state.subscriberId,
        welcomeMessageId: this.$store.state.welcomeMessage?.welcomeMessage.id,
        device: this.$store.state.device
      })
      if (await this.$store.dispatch('redirectWelcomeMessageToWizard')) return
      this.$store.dispatch('toggleOpen')

      if (this.bubble.isOpen) {
        this.$store.dispatch('welcomeMessageClick')
        this.notifyWelcomeMessageClicked()
        this.$nextTick(scrollToLastMessage)
      }
    },
    onTabChanged (tab: string): void {
      this.setCurrentTab(null)
      setTimeout(() => {
        this.setCurrentTab(tab)
        if (tab === 'chat') this.$nextTick(scrollToLastMessage)
      }, 10)
    },
    isTab (tab: string): boolean {
      return this.currentTab === tab
    },
    loadFeatureFlags (): void {
      if (!this.isEnabledABTest) loadFeatureFlags(this.$store.state.customerId, this.$store.state.subscriberId)
    },
    installFocusTrap () {
      window.addEventListener('keydown', this.focusTrap.bind(this))
    },
    focusTrap (event) {
      if (!this.bubble.isOpen) return

      const TAB_KEY = 9
      const ESCAPE_KEY = 27
      const focusableElements = this.getFocusableElements()
      const firstFocusableElement = focusableElements[0]
      const lastFocusableElement = focusableElements[focusableElements.length - 1]

      if (event.key === 'Tab' || event.keyCode === TAB_KEY) {
        if (event.shiftKey) {
          if (document.activeElement === firstFocusableElement) {
            lastFocusableElement.focus()
            event.preventDefault()
          }
        } else {
          if (document.activeElement === lastFocusableElement) {
            firstFocusableElement.focus()
            event.preventDefault()
          }
        }
      }

      if (event.key === 'Escape' || event.keyCode === ESCAPE_KEY) {
        this.$store.dispatch('toggleOpen')
        event.preventDefault()
      }
    },
    getFocusableElements () {
      const focusableElements = [].slice.call(
        this.$refs.main.querySelectorAll(
          "a[href], button, textarea, input, select, [role=button], [tabindex='0']"
        )
      ).filter(function (element) {
        const rect = element.getBoundingClientRect()
        const style = window.getComputedStyle(element)

        return (
          rect.width > 0 &&
          rect.height > 0 &&
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= window.innerHeight &&
          rect.right <= window.innerWidth &&
          style.display !== 'none' &&
          style.visibility !== 'hidden'
        )
      })

      return focusableElements
    },
    focusFirstInteractiveElement () {
      const elements = this.getFocusableElements()
      if (elements.length === 0) return

      elements[0].focus()
    },
    focusChatBubble () {
      this.$refs.chatBubble.$el.focus()
    }
  }
}
</script>

<style src="./main.scss" />
<style src="./mobile.css" />
